import { Button } from 'antd';
import React, { useState, useMemo, useEffect } from 'react';
import {
    getPracticeOverviewTrend,
    getPracticeOverviewByDim,
    getPracticeOverviewPeakHours,
    getPracticeOverviewFutureAppointments,
    getPracticeOverviewFutureByDim,
} from 'api/group';
import {
    TPracticeOverviewSummaryVo,
    TPracticeOverviewTrendVo,
    TPracticeOverviewByDimVo,
    TPracticeOverviewPeakHoursVo,
    TPracticeOverviewFutureApptVo,
    TPracticeOverviewRange,
    TPracticeOverviewFutureRange,
} from '../../types';
import RangeDropdown from '../RangeDropdown';
import s from './s.module.less';

type Props = {
    summary: TPracticeOverviewSummaryVo | null;
};

/* ── helpers ────────────────────────────────────────────── */

const fmt = (v: number | null | undefined) => String(v ?? 0);

const formatDateLabel = (dateStr: string) => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/* ── LineChart ───────────────────────────────────────────── */

const LineChart: React.FC<{ data: number[]; labels: string[] }> = ({ data, labels }) => {
    const W = 1000;
    const H = 280;
    const PAD = { t: 16, r: 16, b: 28, l: 44 };
    const plotW = W - PAD.l - PAD.r;
    const plotH = H - PAD.t - PAD.b;

    if (data.length === 0) return null;

    const rawMax = Math.max(...data);
    const max = rawMax > 0 ? rawMax * 1.15 : 10;
    const min = 0;
    const range = max - min;

    const points = data.map((v, i) => ({
        x: PAD.l + (data.length === 1 ? plotW / 2 : (i / (data.length - 1)) * plotW),
        y: PAD.t + plotH - ((v - min) / range) * plotH,
    }));

    let linePath = `M${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
        const cx1 = (points[i - 1].x + points[i].x) / 2;
        const cy1 = points[i - 1].y;
        const cx2 = (points[i - 1].x + points[i].x) / 2;
        const cy2 = points[i].y;
        linePath += ` C${cx1},${cy1} ${cx2},${cy2} ${points[i].x},${points[i].y}`;
    }
    const areaPath = `${linePath} L${points[points.length - 1].x},${PAD.t + plotH} L${points[0].x},${PAD.t + plotH} Z`;

    const gridCount = 4;
    const gridLines = Array.from({ length: gridCount + 1 }, (_, i) => {
        const y = PAD.t + (i / gridCount) * plotH;
        const val = Math.round(max - (i / gridCount) * range);
        return { y, val };
    });

    const xStep = Math.max(1, Math.floor(labels.length / 6));
    const xLabels = labels
        .map((lbl, i) => ({ lbl: formatDateLabel(lbl), x: points[i]?.x ?? 0 }))
        .filter((_, i) => i % xStep === 0 || i === labels.length - 1);

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className={s.lineSvg} preserveAspectRatio="xMidYMid meet">
            <defs>
                <linearGradient id="apptGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0E62F4" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#0E62F4" stopOpacity="0" />
                </linearGradient>
            </defs>
            {gridLines.map((g, i) => (
                <g key={i}>
                    <line x1={PAD.l} y1={g.y} x2={W - PAD.r} y2={g.y} stroke="#f3f4f6" strokeWidth={1} />
                    <text x={PAD.l - 6} y={g.y + 4} textAnchor="end" className={s.axisLabel}>
                        {g.val}
                    </text>
                </g>
            ))}
            <path d={areaPath} fill="url(#apptGrad)" />
            <path d={linePath} fill="none" stroke="#0e62f4" strokeWidth={2} strokeLinecap="round" />
            {points.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r={3} fill="#0e62f4" />
            ))}
            {xLabels.map((xl, i) => (
                <text key={i} x={xl.x} y={H - 6} textAnchor="middle" className={s.axisLabel}>
                    {xl.lbl}
                </text>
            ))}
        </svg>
    );
};

/* ── Trend badge ─────────────────────────────────────────── */

const ArrowUp = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
            d="M6 2.5v7M6 2.5L3 5.5M6 2.5l3 3"
            stroke="#065f46"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const ArrowDown = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path
            d="M6 9.5v-7M6 9.5L3 6.5M6 9.5l3-3"
            stroke="#991b1b"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

type TrendBadgeProps = { pct: string; direction: 'up' | 'down'; period: string };

const TrendBadge: React.FC<TrendBadgeProps> = ({ pct, direction, period }) => (
    <div className={s.trend}>
        <span className={`${s.badge} ${direction === 'up' ? s.up : s.down}`}>
            {direction === 'up' ? <ArrowUp /> : <ArrowDown />}
            <span className={`${s.pct} ${direction === 'up' ? s.pctUp : s.pctDown}`}>{pct}</span>
        </span>
        <span className={s.period}>{period}</span>
    </div>
);

/* ── Heatmap ─────────────────────────────────────────────── */

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const HOUR_ORDER = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 0, 1, 2, 3, 4, 5, 6, 7];
const HOURS = HOUR_ORDER.map((h) => {
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
});

const Heatmap: React.FC<{ peakHours: TPracticeOverviewPeakHoursVo | null }> = ({ peakHours }) => {
    const grid = useMemo(() => {
        const matrix: number[][] = Array.from({ length: 24 }, () => Array(7).fill(0));
        if (!peakHours?.cells) return matrix;

        let maxCount = 1;
        for (const cell of peakHours.cells) {
            if (cell.apptCount > maxCount) maxCount = cell.apptCount;
        }

        const hourToIdx: Record<number, number> = {};
        HOUR_ORDER.forEach((h, i) => {
            hourToIdx[h] = i;
        });

        for (const cell of peakHours.cells) {
            const hourIdx = hourToIdx[cell.hour];
            const dayIdx = cell.dayOfWeek - 1;
            if (hourIdx != null && dayIdx >= 0 && dayIdx < 7) {
                const level = maxCount > 0 ? Math.ceil((cell.apptCount / maxCount) * 4) : 0;
                matrix[hourIdx][dayIdx] = Math.min(level, 4);
            }
        }
        return matrix;
    }, [peakHours]);

    return (
        <div>
            <div className={s.heatmap}>
                <div />
                {DAYS.map((d) => (
                    <div key={d} className={s.heatHeader}>
                        {d}
                    </div>
                ))}
                {HOURS.map((hour, ri) => (
                    <React.Fragment key={hour}>
                        <div className={s.heatLabel}>{hour}</div>
                        {DAYS.map((day, ci) => (
                            <div key={`${ri}-${ci}`} className={`${s.heatCell} ${s[`heat${grid[ri][ci]}`]}`} />
                        ))}
                    </React.Fragment>
                ))}
            </div>
            <div className={s.heatLegend}>
                <span>Less</span>
                <div className={`${s.heatLegendCell} ${s.heat0}`} />
                <div className={`${s.heatLegendCell} ${s.heat1}`} />
                <div className={`${s.heatLegendCell} ${s.heat2}`} />
                <div className={`${s.heatLegendCell} ${s.heat3}`} />
                <div className={`${s.heatLegendCell} ${s.heat4}`} />
                <span>More</span>
            </div>
        </div>
    );
};

/* ── DimBreakdown (pie + legend) ────────────────────────── */

const COLORS = ['#0E62F4', '#38BDF8', '#A78BFA', '#FB923C', '#34D399', '#F472B6'];

const DimBreakdown: React.FC<{ data: TPracticeOverviewByDimVo | null; label: string }> = ({ data, label }) => {
    if (!data?.rows?.length) return <div className={s.empty}>No {label} data</div>;

    const total = data.rows.reduce((sum, r) => sum + r.scheduled, 0);
    const size = 200;
    const cx = size / 2;
    const cy = size / 2;
    const outerR = 90;
    const innerR = 50;

    let startAngle = -Math.PI / 2;
    const paths = data.rows.map((row, i) => {
        const fraction = total > 0 ? row.scheduled / total : 0;
        const color = COLORS[i % COLORS.length];
        if (fraction >= 1) {
            const d = [
                `M ${cx} ${cy - outerR}`,
                `A ${outerR} ${outerR} 0 1 1 ${cx} ${cy + outerR}`,
                `A ${outerR} ${outerR} 0 1 1 ${cx} ${cy - outerR}`,
                `M ${cx} ${cy - innerR}`,
                `A ${innerR} ${innerR} 0 1 0 ${cx} ${cy + innerR}`,
                `A ${innerR} ${innerR} 0 1 0 ${cx} ${cy - innerR}`,
                'Z',
            ].join(' ');
            return { d, color, row };
        }
        const sweep = fraction * Math.PI * 2;
        const endAngle = startAngle + sweep;
        const largeArc = sweep > Math.PI ? 1 : 0;
        const d = [
            `M ${cx + Math.cos(startAngle) * outerR} ${cy + Math.sin(startAngle) * outerR}`,
            `A ${outerR} ${outerR} 0 ${largeArc} 1 ${cx + Math.cos(endAngle) * outerR} ${cy + Math.sin(endAngle) * outerR}`,
            `L ${cx + Math.cos(endAngle) * innerR} ${cy + Math.sin(endAngle) * innerR}`,
            `A ${innerR} ${innerR} 0 ${largeArc} 0 ${cx + Math.cos(startAngle) * innerR} ${cy + Math.sin(startAngle) * innerR}`,
            'Z',
        ].join(' ');
        startAngle = endAngle;
        return { d, color, row };
    });

    return (
        <div className={s.pieWrap}>
            <div className={s.pieCanvasWrap}>
                <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
                    {paths.map((p, i) => (
                        <path key={i} d={p.d} fill={p.color} fillRule="evenodd" />
                    ))}
                    <text x={cx} y={cy - 4} textAnchor="middle" dominantBaseline="middle" className={s.pieTotal}>
                        {total}
                    </text>
                    <text x={cx} y={cy + 14} textAnchor="middle" dominantBaseline="middle" className={s.pieTotalLabel}>
                        Total
                    </text>
                </svg>
            </div>
            <div className={s.pieLegend}>
                {data.rows.map((row, i) => (
                    <div key={row.dimValue} className={s.pieLegendItem}>
                        <span className={s.pieLegendDot} style={{ background: COLORS[i % COLORS.length] }} />
                        <span className={s.pieLegendName}>{row.dimValue}</span>
                        <span className={s.pieLegendVal}>
                            {row.scheduled} ({row.sharePct?.toFixed(0) ?? 0}%)
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ── Chip options ─────────────────────────────────────────── */

const CHIP_OPTIONS = ['All', 'By specialty', 'By visit type'];

/* ── AppointmentsSection ─────────────────────────────────── */

type PastTabKey =
    | 'scheduled'
    | 'completed'
    | 'patientCanceled'
    | 'providerCanceled'
    | 'noshow'
    | 'rescheduled'
    | 'peak';
type FutureTabKey = 'scheduled' | 'fillRate';

const PAST_TABS: { key: PastTabKey; label: string; metric: string }[] = [
    { key: 'scheduled', label: 'Scheduled', metric: 'SCHEDULED' },
    // { key: 'completed', label: 'Completed', metric: 'Completed' },
    { key: 'patientCanceled', label: 'Patient canceled', metric: 'PATIENT_CANCELED' },
    { key: 'providerCanceled', label: 'Provider canceled', metric: 'PROVIDER_CANCELED' },
    { key: 'noshow', label: 'No-show', metric: 'NO_SHOW' },
    { key: 'rescheduled', label: 'Rescheduled', metric: 'RESCHEDULED' },
    { key: 'peak', label: 'Peak', metric: 'Peak' },
];

const FUTURE_TABS: { key: FutureTabKey; label: string }[] = [
    { key: 'scheduled', label: 'Scheduled' },
];

const DEFAULT_PAST_RANGE: TPracticeOverviewRange = 'LAST_30D';
const DEFAULT_FUTURE_RANGE: TPracticeOverviewFutureRange = 'NEXT_30D';

const AppointmentsSection: React.FC<Props> = ({ summary }) => {
    const [pastRange, setPastRange] = useState<TPracticeOverviewRange>(DEFAULT_PAST_RANGE);
    const [futureRange, setFutureRange] = useState<TPracticeOverviewFutureRange>(DEFAULT_FUTURE_RANGE);
    const [pastTab, setPastTab] = useState<PastTabKey>('scheduled');
    const [pastMetricKey, setPastMetricKey] = useState<string>('SCHEDULED');
    const [futureTab, setFutureTab] = useState<FutureTabKey>('scheduled');
    const [pastChip, setPastChip] = useState('All');
    const [futureChip, setFutureChip] = useState('All');

    const [trend, setTrend] = useState<TPracticeOverviewTrendVo | null>(null);
    const [bySpecialty, setBySpecialty] = useState<TPracticeOverviewByDimVo | null>(null);
    const [byVisitType, setByVisitType] = useState<TPracticeOverviewByDimVo | null>(null);
    const [peakHours, setPeakHours] = useState<TPracticeOverviewPeakHoursVo | null>(null);
    const [futureAppt, setFutureAppt] = useState<TPracticeOverviewFutureApptVo | null>(null);
    const [futureBySpecialty, setFutureBySpecialty] = useState<TPracticeOverviewByDimVo | null>(null);
    const [futureByVisitType, setFutureByVisitType] = useState<TPracticeOverviewByDimVo | null>(null);

    useEffect(() => {
        const filter = { range: pastRange };
        Promise.all([
            getPracticeOverviewTrend({ ...filter, metric: pastMetricKey as any }),
            getPracticeOverviewByDim({ ...filter, dimType: 'SPECIALTY' }),
            getPracticeOverviewByDim({ ...filter, dimType: 'VISIT_TYPE' }),
            getPracticeOverviewPeakHours(filter),
        ]).then(([trendRes, specRes, vtRes, peakRes]) => {
            if (!trendRes.error) setTrend(trendRes?.data?.data ?? null);
            if (!specRes.error) setBySpecialty(specRes?.data?.data ?? null);
            if (!vtRes.error) setByVisitType(vtRes?.data?.data ?? null);
            if (!peakRes.error) setPeakHours(peakRes?.data?.data ?? null);
        });
    }, [pastRange, pastMetricKey]);

    useEffect(() => {
        const filter = { range: futureRange };
        Promise.all([
            getPracticeOverviewFutureAppointments(filter),
            getPracticeOverviewFutureByDim({ ...filter, dimType: 'SPECIALTY' }),
            getPracticeOverviewFutureByDim({ ...filter, dimType: 'VISIT_TYPE' }),
        ]).then(([futRes, specRes, vtRes]) => {
            if (!futRes.error) setFutureAppt(futRes?.data?.data ?? null);
            if (!specRes.error) setFutureBySpecialty(specRes?.data?.data ?? null);
            if (!vtRes.error) setFutureByVisitType(vtRes?.data?.data ?? null);
        });
    }, [futureRange]);

    const trendLabels = useMemo(() => (trend?.series ?? []).map((p) => p.date), [trend]);
    const trendValues = useMemo(() => (trend?.series ?? []).map((p) => p.value ?? 0), [trend]);

    const futureLabels = useMemo(() => (futureAppt?.series ?? []).map((p) => p.date), [futureAppt]);
    const futureValues = useMemo(() => (futureAppt?.series ?? []).map((p) => p.scheduled), [futureAppt]);

    const getPastMetric = (tab: PastTabKey) => {
        if (!summary) return { value: '0', prior: 'from 0', pct: '0%', dir: 'up' as const };
        switch (tab) {
            case 'scheduled':
                return {
                    value: fmt(summary.scheduled),
                    prior: `from ${fmt(summary.scheduledPrior)}`,
                    pct: `${Math.abs(summary.scheduledChangePct ?? 0).toFixed(1)}%`,
                    dir: (summary.scheduledChangePct ?? 0) >= 0 ? ('up' as const) : ('down' as const),
                };
            case 'completed':
                return {
                    value: fmt(summary.scheduled),
                    prior: `from ${fmt(summary.scheduledPrior)}`,
                    pct: `${Math.abs(summary.scheduledChangePct ?? 0).toFixed(1)}%`,
                    dir: (summary.scheduledChangePct ?? 0) >= 0 ? ('up' as const) : ('down' as const),
                };
            case 'patientCanceled':
                return {
                    value: fmt(summary.canceled),
                    prior: `from ${fmt(summary.canceledPrior)}`,
                    pct: `${Math.abs(summary.canceledChangePct ?? 0).toFixed(1)}%`,
                    dir: (summary.canceledChangePct ?? 0) >= 0 ? ('up' as const) : ('down' as const),
                };
            case 'providerCanceled':
                return {
                    value: fmt(summary.canceled),
                    prior: `from ${fmt(summary.canceledPrior)}`,
                    pct: `${Math.abs(summary.canceledChangePct ?? 0).toFixed(1)}%`,
                    dir: (summary.canceledChangePct ?? 0) >= 0 ? ('up' as const) : ('down' as const),
                };
            case 'noshow':
                return {
                    value: fmt(summary.noShow),
                    prior: `from ${fmt(summary.noShowPrior)}`,
                    pct: `${Math.abs(summary.noShowChangePct ?? 0).toFixed(1)}%`,
                    dir: (summary.noShowChangePct ?? 0) >= 0 ? ('up' as const) : ('down' as const),
                };
            case 'rescheduled':
                return {
                    value: fmt(summary.rescheduled),
                    prior: `from ${fmt(summary.rescheduledPrior)}`,
                    pct: `${Math.abs(summary.rescheduledChangePct ?? 0).toFixed(1)}%`,
                    dir: (summary.rescheduledChangePct ?? 0) >= 0 ? ('up' as const) : ('down' as const),
                };
            default:
                return { value: '', prior: '', pct: '', dir: 'up' as const };
        }
    };

    const pastMetric = getPastMetric(pastTab);

    const futureScheduled = futureAppt?.totalScheduled ?? 0;
    const futurePrior = futureAppt?.totalScheduledPrior ?? 0;
    const futureChangePct = futureAppt?.totalScheduledChangePct ?? 0;
    const futureMetric = {
        value: fmt(futureScheduled),
        prior: `from ${fmt(futurePrior)}`,
        pct: `${Math.abs(futureChangePct).toFixed(1)}%`,
        dir: (futureChangePct >= 0 ? 'up' : 'down') as 'up' | 'down',
    };

    const renderPastChipContent = () => {
        if (pastChip === 'By specialty') return <DimBreakdown data={bySpecialty} label="specialty" />;
        if (pastChip === 'By visit type') return <DimBreakdown data={byVisitType} label="visit type" />;
        return <LineChart data={trendValues} labels={trendLabels} />;
    };

    const renderFutureChipContent = () => {
        if (futureChip === 'By specialty') return <DimBreakdown data={futureBySpecialty} label="specialty" />;
        if (futureChip === 'By visit type') return <DimBreakdown data={futureByVisitType} label="visit type" />;
        return <LineChart data={futureValues} labels={futureLabels} />;
    };

    const fillPct = 78; // placeholder until API provides fill rate data
    const filledSlots = 69;
    const totalSlots = 89;

    return (
        <div className={s.wrap}>
            {/* Past Appointments */}
            <div className={s.half}>
                <div className={s.secHdr}>
                    <h2 className={s.secTitle}>Past Appointments</h2>
                    <RangeDropdown value={pastRange} onChange={(v) => setPastRange(v as TPracticeOverviewRange)} />
                </div>
                <div className={s.card}>
                    <div className={s.cardBody}>
                        <div className={s.tabs}>
                            {PAST_TABS.map((tab) => (
                                <span
                                    key={tab.key}
                                    className={`${s.tab} ${pastTab === tab.key ? s.tabActive : ''}`}
                                    onClick={() => {
                                        setPastTab(tab.key);
                                        setPastMetricKey(tab.metric);
                                    }}
                                >
                                    {tab.label}
                                </span>
                            ))}
                        </div>

                        {pastTab === 'peak' ? (
                            <Heatmap peakHours={peakHours} />
                        ) : (
                            <>
                                <div className={s.apptTop}>
                                    <div className={s.apptTopLeft}>
                                        <div className={s.valRow}>
                                            <span className={s.val}>{pastMetric.value}</span>
                                            <span className={s.from}>{pastMetric.prior}</span>
                                        </div>
                                        <TrendBadge
                                            pct={pastMetric.pct}
                                            direction={pastMetric.dir}
                                            period="from last period"
                                        />
                                    </div>
                                    {pastTab === 'scheduled' && (
                                        <div className={s.chips}>
                                            {CHIP_OPTIONS.map((c) => (
                                                <span
                                                    key={c}
                                                    className={`${s.chip} ${pastChip === c ? s.chipActive : ''}`}
                                                    onClick={() => setPastChip(c)}
                                                >
                                                    {c}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className={s.chartWrap}>
                                    {pastTab === 'scheduled' ? (
                                        renderPastChipContent()
                                    ) : (
                                        <LineChart data={trendValues} labels={trendLabels} />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Future Appointments */}
            <div className={s.half}>
                <div className={s.secHdr}>
                    <h2 className={s.secTitle}>Future Appointments</h2>
                    <RangeDropdown
                        value={futureRange}
                        onChange={(v) => setFutureRange(v as TPracticeOverviewFutureRange)}
                        mode="future"
                    />
                </div>
                <div className={s.card}>
                    <div className={s.cardBody}>
                        <div className={s.tabs}>
                            {FUTURE_TABS.map((tab) => (
                                <button
                                    key={tab.key}
                                    className={`${s.tab} ${futureTab === tab.key ? s.tabActive : ''}`}
                                    onClick={() => setFutureTab(tab.key)}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {futureTab === 'scheduled' ? (
                            <>
                                <div className={s.apptTop}>
                                    <div className={s.apptTopLeft}>
                                        <div className={s.valRow}>
                                            <span className={s.val}>{futureMetric.value}</span>
                                            <span className={s.from}>{futureMetric.prior}</span>
                                        </div>
                                        <TrendBadge
                                            pct={futureMetric.pct}
                                            direction={futureMetric.dir}
                                            period="vs same period last month"
                                        />
                                    </div>
                                    <div className={s.chips}>
                                        {CHIP_OPTIONS.map((c) => (
                                            <button
                                                key={c}
                                                className={`${s.chip} ${futureChip === c ? s.chipActive : ''}`}
                                                onClick={() => setFutureChip(c)}
                                            >
                                                {c}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className={s.chartWrap}>{renderFutureChipContent()}</div>
                            </>
                        ) : (
                            <div style={{ marginTop: 16 }}>
                                <div className={s.valRow}>
                                    <span className={s.val}>{fillPct}%</span>
                                </div>
                                <TrendBadge pct="8%" direction="up" period="vs same period last month" />
                                <div className={s.progressWrap}>
                                    <div className={s.progressHeader}>
                                        <span className={s.progressLabel}>0%</span>
                                        <span className={s.progressValue}>{fillPct}%</span>
                                        <span className={s.progressLabel}>100%</span>
                                    </div>
                                    <div className={s.progressBar}>
                                        <div className={s.progressFill} style={{ width: `${fillPct}%` }} />
                                    </div>
                                    <div className={s.progressFooter}>
                                        <span>
                                            {filledSlots} of {totalSlots} slots filled
                                        </span>
                                        <span className={s.progressOpen}>{totalSlots - filledSlots} open slots</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentsSection;
